import React, { useEffect, useRef, useState } from 'react';
import { DatePicker } from '@shopify/polaris';
import { AlertTriangleIcon, CalendarIcon, ChevronDownIcon, ExportIcon, PlayIcon } from '@shopify/polaris-icons';
import { Ic } from './ui';

const API_BASE_URL = window.location.origin;

const TEAMS = [
  { id: 'washington-spirit', label: 'Washington Spirit', csvPrefix: 'spirit-namekits' },
  { id: 'gotham-fc', label: 'Gotham FC', csvPrefix: 'gotham-namekits' },
];

const SEASON_START = { month: 1, day: 26 };   // Feb 26: when the season's kits go on sale

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function seasonToDate() {
  const today = new Date();
  return { start: new Date(today.getFullYear(), SEASON_START.month, SEASON_START.day), end: today };
}

const PRESETS = [
  ['Season to date', seasonToDate],
  ['Last 30 days', () => ({ start: new Date(Date.now() - 30 * 86400000), end: new Date() })],
  ['Last 90 days', () => ({ start: new Date(Date.now() - 90 * 86400000), end: new Date() })],
  ['This month', () => { const t = new Date(); return { start: new Date(t.getFullYear(), t.getMonth(), 1), end: t }; }],
];

// Reads the report's SSE stream, calling onEvent for each event, until it ends.
async function streamReport(url, onEvent) {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}`, Accept: 'text/event-stream' },
  });
  if (!response.ok) throw new Error((await response.text()) || `Server error ${response.status}`);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const event = JSON.parse(line.slice(6));
      if (event.error) throw new Error(event.error);
      onEvent(event);
    }
  }
}

function DateRange({ range, onChange }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(range);
  const [{ month, year }, setNav] = useState({ month: range.start.getMonth(), year: range.start.getFullYear() });
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const show = () => { setPending(range); setNav({ month: range.start.getMonth(), year: range.start.getFullYear() }); setOpen((o) => !o); };
  const pick = (r) => { onChange(r); setOpen(false); };
  const apply = () => { if (pending.start && pending.end) pick(pending); };

  return (
    <div className="uh-daterange" ref={popRef}>
      <button type="button" className="uh-btn" aria-expanded={open} onClick={show}>
        <Ic source={CalendarIcon} sm />{fmt(range.start)} – {fmt(range.end)}<Ic source={ChevronDownIcon} sm />
      </button>
      {open && (
        <div className="uh-daterange-pop">
          <div className="presets">
            {PRESETS.map(([label, make]) => <button key={label} type="button" onClick={() => pick(make())}>{label}</button>)}
          </div>
          <div className="cal">
            <DatePicker
              month={month}
              year={year}
              onChange={setPending}
              onMonthChange={(m, y) => setNav({ month: m, year: y })}
              selected={pending}
              allowRange
              disableDatesAfter={new Date()}
            />
            <div className="acts">
              <button type="button" className="uh-btn slim" onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className="uh-btn primary slim" onClick={apply} disabled={!pending.start || !pending.end}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NameKitReports() {
  const [teamId, setTeamId] = useState(TEAMS[0].id);
  const [range, setRange] = useState(seasonToDate);
  const [results, setResults] = useState({});       // team id -> finished report; switching tabs keeps both
  const [running, setRunning] = useState(null);     // team id while a report streams
  const [progress, setProgress] = useState({ pct: 0, status: '' });
  const [error, setError] = useState('');

  const team = TEAMS.find((t) => t.id === teamId);
  const result = results[teamId];

  const runReport = async () => {
    const start = toISODate(range.start);
    const end = toISODate(range.end);
    setRunning(teamId);
    setProgress({ pct: 0, status: 'Starting…' });
    setError('');
    try {
      await streamReport(`${API_BASE_URL}/api/name-kit-reports/${teamId}?start_date=${start}&end_date=${end}`, (event) => {
        if (event.progress !== undefined || event.status) setProgress((p) => ({ pct: event.progress ?? p.pct, status: event.status ?? p.status }));
        if (event.result) setResults((r) => ({ ...r, [teamId]: event.result }));
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(null);
    }
  };

  const exportCSV = () => {
    const quote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      ['Order', 'Date', 'Product', 'Player Name', 'Qty', 'Channel', 'Location'].join(','),
      ...result.rows.map((r) => [r.order, r.created_at, quote(r.product), quote(r.name), r.qty, r.channel, quote(r.location)].join(',')),
    ];
    const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${team.csvPrefix}-${result.date_range.start}-to-${result.date_range.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = result?.summary;
  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Name Kit Reports</h1>
          <div className="uh-sub">Player-name customizations sold on each club's store — online and rung at the stadium — by player.</div>
        </div>
        {s && (
          <div className="uh-statcard" aria-label={`${team.label} summary`}>
            <div className="stats">
              <div className="uh-stat"><span className="v">{s.total_customized_jerseys.toLocaleString()}</span><span className="k">Customized jerseys</span></div>
              <div className="uh-stat"><span className="v">{s.online.toLocaleString()}</span><span className="k">Online</span></div>
              <div className="uh-stat"><span className="v">{s.in_stadium.toLocaleString()}</span><span className="k">In-stadium</span></div>
              <div className="uh-stat"><span className="v">{s.unique_names}</span><span className="k">Players</span></div>
              <div className="uh-stat"><span className="v">{s.orders_with_customization.toLocaleString()}</span><span className="k">Orders with a name</span></div>
            </div>
          </div>
        )}
      </div>

      <div className="uh-card uh-nk-card">
        <div className="uh-tabs" role="tablist" aria-label="Club">
          {TEAMS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={teamId === t.id} onClick={() => setTeamId(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="uh-nk-tbar">
          <DateRange range={range} onChange={setRange} />
          <button type="button" className="uh-btn primary" onClick={runReport} disabled={running !== null}>
            <Ic source={PlayIcon} sm />Run Report
          </button>
          {running === teamId && (
            <span className="uh-prog" role="status">
              <span className="bar"><i style={{ width: `${progress.pct}%` }} /></span>{progress.status}
            </span>
          )}
          <span className="uh-sp" />
          {result && (
            <>
              <span className="uh-help">{result.date_range.start} to {result.date_range.end} · {s.total_orders_scanned.toLocaleString()} orders read</span>
              <button type="button" className="uh-btn" onClick={exportCSV} disabled={!result.rows.length}><Ic source={ExportIcon} sm />Export CSV</button>
            </>
          )}
        </div>

        {error && (
          <div className="uh-card-b">
            <div className="uh-alert crit"><Ic source={AlertTriangleIcon} sm /><div>{error}</div></div>
          </div>
        )}

        {!result && !error && (
          <div className="uh-empty">{running === teamId ? progress.status : `Pick a date range and run the ${team.label} report.`}</div>
        )}

        {result && (result.breakdown.length === 0 ? (
          <div className="uh-empty">No customizations in this range.</div>
        ) : (
          <table className="uh-tbl uh-nk-tbl">
            <thead>
              <tr><th>Player</th><th className="r">Online</th><th className="r">In-stadium</th><th className="r">Total</th></tr>
            </thead>
            <tbody>
              {result.breakdown.map((b) => (
                <tr key={b.name}>
                  <td className="nm">{b.name}</td>
                  <td className="r uh-num">{b.online.toLocaleString()}</td>
                  <td className="r uh-num">{b.in_stadium.toLocaleString()}</td>
                  <td className="r uh-num">{b.count.toLocaleString()}</td>
                </tr>
              ))}
              <tr className="tot">
                <td>Total</td>
                <td className="r uh-num">{s.online.toLocaleString()}</td>
                <td className="r uh-num">{s.in_stadium.toLocaleString()}</td>
                <td className="r uh-num">{s.total_customized_jerseys.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
}

export default NameKitReports;
